import { createContext, useContext, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "./AuthContext";
import { useToast } from "./ToastContext";
import notificationService from "../services/notificationService";
import api from "../services/api";

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const lastNotificationIdRef = useRef(null);

  // Fetch notification settings
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const response = await api.get("/settings");
      return response.data;
    },
    enabled: isAuthenticated,
  });

  // Global polling for notifications
  const { data: notifications } = useQuery({
    queryKey: ["notifications", "global-poll"],
    queryFn: async () => {
      const data = await notificationService.getNotifications({ limit: 5 });
      return data.items;
    },
    enabled: isAuthenticated,
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Handle new notifications and trigger toasts
  useEffect(() => {
    if (!notifications || notifications.length === 0 || !settings) return;

    const latestNotification = notifications[0];

    // Initialize ref on first load with the latest ID to avoid toasting existing ones
    if (lastNotificationIdRef.current === null) {
      lastNotificationIdRef.current = latestNotification.id;
      return;
    }

    // If it's a new notification and unread
    if (
      latestNotification.id !== lastNotificationIdRef.current &&
      !latestNotification.is_read
    ) {
      lastNotificationIdRef.current = latestNotification.id;

      const type = latestNotification.notification_type;
      let shouldShowToast = false;

      // Map notification types to settings
      if (type === "job" && settings.job_alerts) {
        shouldShowToast = true;
      } else if (
        (type === "message" || type === "ticket_reply") &&
        settings.message_notifications
      ) {
        shouldShowToast = true;
      } else if (type === "event" && settings.interview_reminders) {
        shouldShowToast = true;
      } else if (type === "training" && settings.training_updates) {
        shouldShowToast = true;
      } else if (type === "alert" || type === "achievement") {
        // Always show important alerts or achievements
        shouldShowToast = true;
      }

      if (shouldShowToast) {
        toast.info(latestNotification.message, {
          title: latestNotification.title,
        });
      }
    }
  }, [notifications, settings, toast]);

  return (
    <NotificationContext.Provider value={{}}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotification must be used within a NotificationProvider");
  }
  return context;
};
